/**
 * LeaguePlayoffsPage — /league/:leagueId/playoffs
 * ─────────────────────────────────────────────────────────────────
 * League gap-closure Phase F (2026-06-09). Single-elimination
 * playoff brackets seeded from standings.
 *
 * Backend: routes/league-playoffs.js. A bracket is rounds of SLOTS;
 * each slot can be backed by a real league game (scheduled here,
 * then it behaves like any league game — game sheet, officials,
 * scoresheet lock). Winners advance automatically; when a backed
 * game is locked with a score the UI suggests the winner.
 *
 * Reads for any member; create/schedule/winner actions for
 * manage_schedule (managers implicitly) — backend re-checks.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Trophy, Plus, ChevronUp, ChevronDown, Trash2, Calendar, Crown } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api.js';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';
import LeagueSubNav from './LeagueSubNav.jsx';

export default function LeaguePlayoffsPage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const canEdit = isManager || can('manage_schedule');

  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonIdState] = useState(null);
  // 2026-09-28 — ?season=<id> deep link; the picker keeps it in sync.
  const [searchParams, setSearchParams] = useSearchParams();
  const setSeasonId = (id) => {
    setSeasonIdState(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('season', id); else next.delete('season');
    setSearchParams(next, { replace: true });
  };
  const [brackets, setBrackets] = useState(null);
  const [openBracketId, setOpenBracketId] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/leagues/${leagueId}/seasons`)
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.seasons || [];
        setSeasons(list);
        const wanted = searchParams.get('season');
        const fromUrl = wanted && list.find(s => s.id === wanted);
        const active = list.find(s => s.status === 'active');
        setSeasonIdState((fromUrl || active || list[0])?.id || null);
      })
      .catch(() => { if (!cancelled) setSeasons([]); });
    return () => { cancelled = true; };
  }, [leagueId]);

  const loadBrackets = useCallback(async () => {
    if (!seasonId) return;
    try {
      const { data } = await api.get(`/leagues/${leagueId}/seasons/${seasonId}/playoffs`);
      setBrackets(data?.brackets || []);
      // Auto-open the only bracket.
      if ((data?.brackets || []).length === 1) setOpenBracketId(data.brackets[0].id);
    } catch {
      setBrackets([]);
    }
  }, [leagueId, seasonId]);
  useEffect(() => { setBrackets(null); setOpenBracketId(null); loadBrackets(); }, [loadBrackets]);

  if (roleLoading) {
    return <div className="container"><LeagueSubNav league={null} /><div className="card">Loading…</div></div>;
  }

  return (
    <div className="container" style={{ paddingBottom: 60 }}>
      <LeagueSubNav league={null} />

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Trophy size={20} color="var(--ice)" />
          <h1 className="section-title" style={{ margin: 0, flex: 1 }}>Playoffs</h1>
          {canEdit && seasonId && (
            <button className="btn-ice" onClick={() => setCreating(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={15} /> New bracket
            </button>
          )}
        </div>
        {seasons.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 12, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>Season</label>
            <select value={seasonId || ''} onChange={(e) => setSeasonId(e.target.value || null)}
                    style={{ ...inputStyle, maxWidth: 260 }}>
              {seasons.map(s => (
                <option key={s.id} value={s.id}>{s.name}{s.status === 'active' ? ' · active' : ''}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {creating && (
        <CreateBracketModal
          leagueId={leagueId} seasonId={seasonId}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); loadBrackets(); }}
        />
      )}

      {brackets === null && <div className="card">Loading brackets…</div>}
      {brackets?.length === 0 && (
        <div className="card" style={{ color: 'var(--text-2)', fontSize: 14 }}>
          No playoff brackets yet{canEdit ? ' — create one from the standings when the regular season wraps up.' : '.'}
        </div>
      )}
      {brackets?.map(b => (
        <div key={b.id} className="card" style={{ marginBottom: 14 }}>
          <button type="button"
            onClick={() => setOpenBracketId(openBracketId === b.id ? null : b.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', textAlign: 'left', padding: 0 }}>
            <Trophy size={16} color={b.status === 'completed' ? 'var(--puck, #f5a623)' : 'var(--ice)'} />
            <span style={{ fontWeight: 700, fontSize: 15 }}>{b.name}</span>
            {b.division_name && <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{b.division_name}</span>}
            <span style={{ fontSize: 11, color: b.status === 'completed' ? 'var(--puck, #f5a623)' : 'var(--text-2)', marginLeft: 'auto' }}>
              {b.status}
            </span>
          </button>
          {openBracketId === b.id && (
            <BracketView
              leagueId={leagueId} seasonId={seasonId} bracketId={b.id}
              canEdit={canEdit}
              onChanged={loadBrackets}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Create modal — name + optional division + seed order
// ─────────────────────────────────────────────────────────────────
function CreateBracketModal({ leagueId, seasonId, onClose, onCreated }) {
  const [name, setName] = useState('Playoffs');
  const [divisions, setDivisions] = useState([]);
  const [divisionId, setDivisionId] = useState('');
  const [standings, setStandings] = useState(null); // standings payload
  const [seeds, setSeeds] = useState([]);           // [{team_id, team_name}]
  const [saving, setSaving] = useState(false);

  // Standings → default seed order. Re-derives when division changes.
  useEffect(() => {
    let cancelled = false;
    api.get(`/leagues/${leagueId}/seasons/${seasonId}/standings`)
      .then(({ data }) => { if (!cancelled) setStandings(data); })
      .catch(() => { if (!cancelled) setStandings({ divisions: [] }); });
    api.get(`/leagues/${leagueId}/seasons/${seasonId}/divisions`)
      .then(({ data }) => { if (!cancelled) setDivisions(data?.divisions || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leagueId, seasonId]);

  useEffect(() => {
    if (!standings) return;
    const divs = standings.divisions || [];
    const source = divisionId
      ? divs.filter(d => d.division_id === divisionId)
      : divs;
    // Flatten in rank order. League-wide brackets across divisions
    // simply concatenate by division order — manager reorders below.
    const flat = source.flatMap(d => d.teams || []);
    setSeeds(flat.map(t => ({ team_id: t.team_id, team_name: t.team_name })));
  }, [standings, divisionId]);

  function move(idx, dir) {
    setSeeds(prev => {
      const next = prev.slice();
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }
  function removeSeed(idx) {
    setSeeds(prev => prev.filter((_, i) => i !== idx));
  }

  async function create() {
    if (!name.trim()) return toast.error('Name the bracket');
    if (seeds.length < 2) return toast.error('Need at least 2 teams');
    setSaving(true);
    try {
      await api.post(`/leagues/${leagueId}/seasons/${seasonId}/playoffs`, {
        name: name.trim(),
        division_id: divisionId || null,
        seeds: seeds.map(s => s.team_id),
      });
      toast.success('Bracket created');
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not create bracket');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16, border: '1px dashed var(--border)' }}>
      <h2 className="section-title" style={{ fontSize: 15, marginBottom: 10 }}>New playoff bracket</h2>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: '1 1 220px' }}>
          <label style={labelStyle}>Name</label>
          <input type="text" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={labelStyle}>Division (optional)</label>
          <select value={divisionId} onChange={(e) => setDivisionId(e.target.value)} style={inputStyle}>
            <option value="">League-wide</option>
            {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      </div>

      <label style={labelStyle}>
        Seeds (1 = top seed — prefilled from standings; reorder or remove teams that didn't qualify)
      </label>
      {!standings && <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Loading standings…</div>}
      {seeds.map((s, i) => (
        <div key={s.team_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 14 }}>
          <span style={{ width: 26, textAlign: 'right', color: 'var(--text-2)', fontWeight: 700 }}>{i + 1}.</span>
          <span style={{ flex: 1 }}>{s.team_name}</span>
          <button type="button" className="btn-ghost" style={miniBtn} disabled={i === 0} onClick={() => move(i, -1)}><ChevronUp size={13} /></button>
          <button type="button" className="btn-ghost" style={miniBtn} disabled={i === seeds.length - 1} onClick={() => move(i, 1)}><ChevronDown size={13} /></button>
          <button type="button" className="btn-ghost" style={{ ...miniBtn, color: 'var(--penalty, #ff4444)' }} onClick={() => removeSeed(i)}><Trash2 size={13} /></button>
        </div>
      ))}
      {seeds.length > 0 && ![2, 4, 8, 16, 32].includes(seeds.length) && (
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6 }}>
          {seeds.length} teams — top seeds get first-round byes.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
        <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn-ice" onClick={create} disabled={saving || seeds.length < 2}>
          {saving ? 'Creating…' : `Create (${seeds.length} teams)`}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Bracket view — rounds as columns
// ─────────────────────────────────────────────────────────────────
function BracketView({ leagueId, seasonId, bracketId, canEdit, onChanged }) {
  const base = `/leagues/${leagueId}/seasons/${seasonId}/playoffs/${bracketId}`;
  const [data, setData] = useState(null);
  const [schedulingSlot, setSchedulingSlot] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(base);
      setData(d);
    } catch {
      setData(null);
    }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  async function setWinner(slot, teamId, teamName) {
    if (!window.confirm(`Record ${teamName} as the winner?`)) return;
    try {
      const { data: d } = await api.post(`${base}/slots/${slot.id}/winner`, { team_id: teamId });
      toast.success(d?.champion ? `🏆 ${teamName} wins the bracket!` : 'Winner recorded');
      load();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not record winner');
    }
  }

  async function remove() {
    if (!window.confirm('Delete this bracket? Scheduled playoff games stay on the schedule (cancel them there if needed).')) return;
    try {
      await api.delete(base);
      toast.success('Bracket deleted');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not delete');
    }
  }

  if (!data) return <div style={{ padding: 14, fontSize: 13, color: 'var(--text-2)' }}>Loading bracket…</div>;

  const rounds = [...new Set(data.slots.map(s => s.round))].sort((a, b) => a - b);
  const maxRound = rounds[rounds.length - 1];
  const roundLabel = (r) => {
    const remaining = maxRound - r;
    if (remaining === 0) return 'Final';
    if (remaining === 1) return 'Semifinals';
    if (remaining === 2) return 'Quarterfinals';
    return `Round ${r}`;
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', gap: 14, overflowX: 'auto', paddingBottom: 8 }}>
        {rounds.map(r => (
          <div key={r} style={{ minWidth: 230, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              {roundLabel(r)}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, justifyContent: 'space-around' }}>
              {data.slots.filter(s => s.round === r).map(slot => (
                <SlotCard key={slot.id}
                  slot={slot} canEdit={canEdit}
                  onSchedule={() => setSchedulingSlot(slot)}
                  onWinner={setWinner}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {canEdit && (
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-ghost" style={{ fontSize: 12, color: 'var(--penalty, #ff4444)' }} onClick={remove}>
            <Trash2 size={13} /> Delete bracket
          </button>
        </div>
      )}
      {schedulingSlot && (
        <ScheduleSlotModal
          leagueId={leagueId} base={base} slot={schedulingSlot}
          onClose={() => setSchedulingSlot(null)}
          onDone={() => { setSchedulingSlot(null); load(); }}
        />
      )}
    </div>
  );
}

function SlotCard({ slot, canEdit, onSchedule, onWinner }) {
  const decided = !!slot.winner_team_id;
  const bothTeams = slot.home_team_id && slot.away_team_id;
  const hasScore = slot.result_home_score != null && slot.result_away_score != null;
  // Suggested winner from a locked backed game.
  const suggested = slot.is_scoresheet_locked && hasScore
    ? (slot.result_home_score > slot.result_away_score ? slot.home_team_id
       : slot.result_away_score > slot.result_home_score ? slot.away_team_id : null)
    : null;

  const Side = ({ teamId, teamName, seed, score }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
      fontSize: 13,
      fontWeight: decided && slot.winner_team_id === teamId ? 800 : 500,
      opacity: decided && slot.winner_team_id !== teamId ? 0.5 : 1,
    }}>
      {seed && <span style={{ fontSize: 10, color: 'var(--text-2)', width: 14 }}>{seed}</span>}
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {teamName || <em style={{ color: 'var(--text-2)' }}>TBD</em>}
      </span>
      {score != null && <span style={{ fontWeight: 700 }}>{score}</span>}
      {decided && slot.winner_team_id === teamId && <Crown size={12} color="var(--puck, #f5a623)" />}
      {canEdit && !decided && bothTeams && teamId && (
        <button type="button" title="Record winner"
          onClick={() => onWinner(slot, teamId, teamName)}
          style={{
            fontSize: 10, padding: '1px 6px', borderRadius: 6, cursor: 'pointer',
            border: suggested === teamId ? '1px solid var(--puck, #f5a623)' : '1px solid var(--border)',
            background: suggested === teamId ? 'rgba(255,170,0,0.12)' : 'transparent',
            color: suggested === teamId ? 'var(--puck, #f5a623)' : 'var(--text-2)',
          }}>
          {suggested === teamId ? 'won ✓' : 'won?'}
        </button>
      )}
    </div>
  );

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-2)', padding: '4px 0' }}>
      <Side teamId={slot.home_team_id} teamName={slot.home_team_name} seed={slot.home_seed} score={hasScore ? slot.result_home_score : null} />
      <div style={{ borderTop: '1px solid var(--border)' }} />
      <Side teamId={slot.away_team_id} teamName={slot.away_team_name} seed={slot.away_seed} score={hasScore ? slot.result_away_score : null} />
      <div style={{ padding: '3px 8px', fontSize: 11, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
        {slot.game_id ? (
          <>
            <Calendar size={11} />
            {slot.game_date} {String(slot.game_time || '').slice(0, 5)}
            {slot.venue_name ? ` · ${slot.venue_name}` : ''}
            {slot.game_status === 'cancelled' ? ' · cancelled' : ''}
          </>
        ) : bothTeams && canEdit && !decided ? (
          <button type="button" className="btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }} onClick={onSchedule}>
            <Calendar size={11} /> Schedule game
          </button>
        ) : !bothTeams ? (
          <span>waiting on previous round</span>
        ) : null}
      </div>
    </div>
  );
}

function ScheduleSlotModal({ leagueId, base, slot, onClose, onDone }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('19:00');
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/leagues/${leagueId}/venues`)
      .then(({ data }) => { if (!cancelled) setVenues(data?.venues || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leagueId]);

  async function save() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return toast.error('Pick a date');
    if (!/^\d{2}:\d{2}$/.test(time)) return toast.error('Pick a time');
    setSaving(true);
    try {
      await api.post(`${base}/slots/${slot.id}/schedule`, {
        game_date: date, game_time: time, venue_id: venueId || null,
      });
      toast.success('Playoff game scheduled — both benches notified');
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not schedule');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      {/* Mobile fix: matches the same fixed-overlay-modal guard used
          elsewhere (e.g. LeagueListPage's CreateLeagueModal) — caps the
          dialog to the visible area and lets it scroll instead of letting
          the Schedule/Cancel buttons get pushed off-screen behind the
          on-screen keyboard on a short phone. */}
      <div className="card" style={{ maxWidth: 420, width: '100%', maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h2 className="section-title" style={{ fontSize: 15, marginBottom: 10 }}>
          Schedule: {slot.home_team_name} vs {slot.away_team_name}
        </h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ flex: '1 1 140px' }}>
            <label style={labelStyle}>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: '1 1 110px' }}>
            <label style={labelStyle}>Time</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={inputStyle} />
          </div>
        </div>
        <label style={labelStyle}>Venue (optional)</label>
        <select value={venueId} onChange={(e) => setVenueId(e.target.value)} style={inputStyle}>
          <option value="">—</option>
          {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-ice" onClick={save} disabled={saving}>{saving ? 'Scheduling…' : 'Schedule'}</button>
        </div>
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 12, color: 'var(--text-2)', marginBottom: 4 };
const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--border)', background: 'var(--bg-2)',
  color: 'var(--text)', fontSize: 14, outline: 'none',
};
const miniBtn = { fontSize: 11, padding: '2px 6px' };
